import React, { useRef, useState } from "react";
import { api, resolveImageUrl, formatApiErrorDetail } from "../lib/api";
import { Image as ImageIcon, Upload, X, Loader2 } from "lucide-react";

const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const MAX_MB = 5;

/**
 * Wiederverwendbarer Bild-Upload (Geschenke, Shop, Partner, …).
 * Props:
 *   - value: aktueller Pfad (string)
 *   - onChange(pathOrEmpty)
 *   - endpoint: API-Endpoint (relativ, z. B. "/admin/shop/bild-upload")
 *   - label: Label-Text (z. B. "Produktbild")
 *   - testId: data-testid Prefix
 */
export default function BildUpload({
  value,
  onChange,
  endpoint = "/admin/geschenke/bild-upload",
  label = "Bild",
  testId = "bild-upload",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [localPreview, setLocalPreview] = useState(null);

  const previewSrc = localPreview || (value ? resolveImageUrl(value) : null);
  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!ALLOWED.includes(file.type)) {
      setError("Nur JPG, PNG oder WebP erlaubt.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Datei zu groß (max ${MAX_MB} MB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLocalPreview(reader.result);
    reader.readAsDataURL(file);

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.path);
      setLocalPreview(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen.");
      setLocalPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    onChange("");
    setLocalPreview(null);
    setError("");
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="block text-sm font-medium text-falke-blue">{label}</label>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-xl border-2 border-dashed border-falke-blue/20 bg-falke-warmwhite flex items-center justify-center overflow-hidden flex-shrink-0 relative" data-testid={`${testId}-preview`}>
          {previewSrc ? (
            <img src={previewSrc} alt="Vorschau" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={28} className="text-falke-blue/40" />
          )}
          {busy && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="animate-spin text-falke-orange" size={22} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={pick} disabled={busy} className="btn-outline !py-2 text-sm inline-flex items-center gap-2" data-testid={`${testId}-btn`}>
              <Upload size={14} /> {value ? "Bild ändern" : "Bild hochladen"}
            </button>
            {value && (
              <button type="button" onClick={remove} disabled={busy} className="text-rose-600 text-sm hover:underline inline-flex items-center gap-1" data-testid={`${testId}-remove`}>
                <X size={14} /> Entfernen
              </button>
            )}
          </div>
          <div className="text-xs text-falke-anthracite/60 mt-1.5">JPG, PNG oder WebP · max. 5 MB · quadratisch empfohlen</div>
          {error && <div className="text-xs text-red-600 mt-1" data-testid={`${testId}-error`}>{error}</div>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={handle}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
